// The site map, for paper.
//
// WHY. The console's signature view is the map: the workability field washed
// over the water, the site marked, the sleeves flying from it. The printable
// report had none of it — two line charts and prose. A client forwarding this
// document was forwarding the least recognisable part of the product.
//
// WHY NOT THE LIVE MAP. Capturing MapLibre's own canvas needs
// preserveDrawingBuffer:true, which disables a real optimisation on EVERY frame
// the console draws, forever, so that a report can be made occasionally. It also
// requires a live map instance, tiles settled and the field loaded, on a page
// that has no map. The whole cost lands on the interactive surface to serve the
// static one.
//
// So this composites instead, from pieces that already exist:
//
//   1. the basemap as ONE static image — Esri's MapServer /export takes a bbox
//      and returns a PNG, which is how the overlays already fetch BOEM and WDPA;
//   2. the field, from map/windfield.ts's own renderer, NOT a second copy of it;
//   3. the marks, drawn here.
//
// WHY IT IS A PLATE AND NOT A SWATCH. The first version was a bare 900×900
// square: field, site, north arrow, scale bar, nothing else. The first question
// asked of a print of it was the right one — WHERE IS THIS? A frame carrying no
// coordinates and no locator is a colour swatch, not a map. So:
//
//   * MARGINS carry a labelled graticule. Latitude down the left, longitude
//     along the bottom, ticks on all four sides. That is the answer to "where",
//     and it is also what lets a reader lay this beside a chart or a nav plot.
//   * A LOCATOR INSET sits in the corner, a wide view with this frame drawn on
//     it as a box. It answers the other "where" — which sea, which coast.
//   * The north arrow and scale bar sit on PLAQUES. Stroked text laid straight
//     over imagery is why they were not seen.
//   * The frame is LANDSCAPE. A square wastes a column of paper on sea that
//     carries no more information than the sea beside it.
//
// EVERYTHING IS INLINED AS A DATA URL because the report has a "download .html"
// that must stand alone on someone else's laptop with no network. Esri answers
// Access-Control-Allow-Origin: *, so the bytes can be read and base64'd; that
// also sidesteps canvas tainting, which would otherwise make toDataURL throw on
// the composite.
//
// PROJECTION. The field image is Mercator (windfield.ts renders it that way, for
// the reason its own comment gives). So the basemap is requested in Mercator —
// bboxSR/imageSR 3857 — and both are drawn into the same box. Requesting the
// basemap in 4326 and stretching the Mercator field over it would misregister
// the coast against the colour, worst at the top and bottom of the frame.
//
// The frame does not straddle the antimeridian. Nothing in the field pipeline
// does either — fieldmesh boxes are plain lon ranges — so this is the same
// limitation, not a new one.

import { renderFieldHour, type FieldLayer, type FieldLimits } from "../map/windfield.js";
import { indexAtInstant, type FieldMesh } from "../forecast/fieldmesh.js";
import { compass16 } from "../data/stormlist.js";

const ESRI_EXPORT =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/export";

/** The plate is designed at this logical width and rendered at a multiple of it. */
const DESIGN_W = 600;

const INK = "#16232e";
const WIND_INK = "#16232e";
const SEA_INK = "#0f6b74";

/** Web-Mercator y for a latitude, in metres. */
function mercY(lat: number): number {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  return 6378137 * Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360));
}
/** Web-Mercator x for a longitude, in metres. */
function mercX(lon: number): number {
  return (6378137 * lon * Math.PI) / 180;
}
/** Inverse of mercY, for turning frame edges back into labelled latitudes. */
function invMercY(y: number): number {
  return (360 / Math.PI) * (Math.atan(Math.exp(y / 6378137)) - Math.PI / 4);
}

export interface SiteMapOptions {
  lat: number;
  lon: number;
  mesh: FieldMesh;
  /** The instant to paint — normally the window's worst hour. */
  instant: number;
  layer: FieldLayer;
  limits: FieldLimits;
  /** Half-HEIGHT of the frame in degrees of latitude. Width follows the aspect. */
  spanDeg?: number;
  widthPx?: number;
  heightPx?: number;
  /** Half-height of the locator inset, degrees. Big enough to name the sea. */
  insetSpanDeg?: number;
  /**
   * Stamped into the plate itself, top-left: normally the position and the hour
   * the field is valid for.
   *
   * IN THE IMAGE, not only in the caption, because the caption is HTML and the
   * PNG is what gets dragged into an email or pasted into a client's own report.
   * A field plot with no position and no valid time is not evidence of anything.
   */
  stamp?: string[];
  /**
   * Optional sleeves at the point: bearing quoted FROM, and 0..1 of the limit.
   *
   * `text` is the READING, already formatted in the operator's units by the
   * caller — "24 kn", "2.1 m". Formatted there rather than here so ui/units.ts
   * stays the one authority on what a number looks like; this module must never
   * become a second place that decides metres or feet.
   */
  wind?: { dirDeg: number | null; frac: number | null; label: string; text?: string };
  sea?: { dirDeg: number | null; frac: number | null; label: string; text?: string };
}

/** The margins of the plate, in logical units — where the graticule is labelled. */
const MARGIN = { left: 46, right: 14, top: 14, bottom: 26 };

/**
 * The geographic bounds this plate will draw, given its frame.
 *
 * Exported because the CALLER has to fetch a field mesh that covers the frame,
 * and the frame's width depends on its aspect and on the latitude — so a caller
 * guessing a square box would leave the mesh's edge showing inside the picture.
 * One place knows the geometry.
 */
export function frameBounds(
  lat: number,
  lon: number,
  spanDeg = 2,
  widthPx = 1200,
  heightPx = 750,
): { latMin: number; latMax: number; lonMin: number; lonMax: number } {
  const s = widthPx / DESIGN_W;
  const mw = widthPx - (MARGIN.left + MARGIN.right) * s;
  const mh = heightPx - (MARGIN.top + MARGIN.bottom) * s;
  const latMin = lat - spanDeg;
  const latMax = lat + spanDeg;
  const halfX = ((mercY(latMax) - mercY(latMin)) / 2) * (mw / mh);
  const xC = mercX(lon);
  return {
    latMin,
    latMax,
    lonMin: (invMercX(xC - halfX)),
    lonMax: (invMercX(xC + halfX)),
  };
}
function invMercX(x: number): number {
  return (x * 180) / (6378137 * Math.PI);
}

/** Fetch a cross-origin image and return it as a data URL. */
async function asDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(typeof fr.result === "string" ? fr.result : null);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function esriUrl(x0: number, y0: number, x1: number, y1: number, w: number, h: number): string {
  return (
    `${ESRI_EXPORT}?bbox=${x0},${y0},${x1},${y1}` +
    `&bboxSR=3857&imageSR=3857&size=${Math.round(w)},${Math.round(h)}` +
    `&format=png&transparent=false&f=image`
  );
}

/**
 * A graticule interval that lands on numbers a reader recognises.
 *
 * Ticks every 0.37° would be arithmetically fine and useless: the labels exist
 * to be compared against a chart, so they have to be chart numbers.
 */
function niceStep(spanDeg: number): number {
  const ladder = [0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 20];
  const want = spanDeg / 4.5;
  for (const v of ladder) if (v >= want) return v;
  return 30;
}
function decimals(step: number): number {
  return step >= 1 ? 0 : step >= 0.5 ? 1 : 2;
}
function fmtLat(v: number, dp: number): string {
  return `${Math.abs(v).toFixed(dp)}°${v < 0 ? "S" : "N"}`;
}
function fmtLon(v: number, dp: number): string {
  const w = ((v + 540) % 360) - 180;
  return `${Math.abs(w).toFixed(dp)}°${w < 0 ? "W" : "E"}`;
}

/**
 * Render the site map and return it as a PNG data URL, or null when the pieces
 * are not available (no field cover, basemap unreachable).
 *
 * Null rather than a partial map: a frame with the coast but no field would look
 * like a calm sea rather than like missing data. That includes the case where
 * the field renders empty — the locator and the graticule would still make a
 * convincing-looking plate, which is exactly the problem.
 */
export async function siteMap(opts: SiteMapOptions): Promise<string | null> {
  const span = opts.spanDeg ?? 2;
  const W = opts.widthPx ?? 1200;
  const H = opts.heightPx ?? Math.round(W * 0.625);
  const S = W / DESIGN_W; // logical unit → device pixels
  const u = (n: number): number => n * S;

  // The map rectangle inside the margins.
  const mx = u(MARGIN.left);
  const my = u(MARGIN.top);
  const mw = W - u(MARGIN.left + MARGIN.right);
  const mh = H - u(MARGIN.top + MARGIN.bottom);

  // The frame, in Mercator metres. The longitude half-width follows the frame's
  // aspect so the projection stays isotropic — a wider plate shows more sea, it
  // does not squash it.
  const latMin = opts.lat - span;
  const latMax = opts.lat + span;
  const yMin = mercY(latMin);
  const yMax = mercY(latMax);
  const halfX = ((yMax - yMin) / 2) * (mw / mh);
  const xC = mercX(opts.lon);
  const xMin = xC - halfX;
  const xMax = xC + halfX;
  const lonMin = invMercX(xMin);
  const lonMax = invMercX(xMax);

  // The locator. Clamped so a high-latitude site still gets a whole box rather
  // than one squashed against the pole.
  const iSpan = opts.insetSpanDeg ?? 20;
  const iLat = Math.max(-82 + iSpan, Math.min(82 - iSpan, opts.lat));
  const iyMin = mercY(iLat - iSpan);
  const iyMax = mercY(iLat + iSpan);
  const iHalf = (iyMax - iyMin) / 2;
  const ixMin = mercX(opts.lon) - iHalf;
  const ixMax = mercX(opts.lon) + iHalf;
  const iSide = Math.round(u(118));

  const [base, inset] = await Promise.all([
    asDataUrl(esriUrl(xMin, yMin, xMax, yMax, mw, mh)),
    asDataUrl(esriUrl(ixMin, iyMin, ixMax, iyMax, iSide, iSide)),
  ]);
  if (base === null) return null;

  const t = indexAtInstant(opts.mesh, opts.instant);
  const fieldUrl = renderFieldHour(opts.mesh, opts.layer, opts.limits, t);
  if (fieldUrl === "") return null;

  const [baseImg, fieldImg, insetImg] = await Promise.all([
    loadImage(base),
    loadImage(fieldUrl),
    inset === null ? Promise.resolve(null) : loadImage(inset),
  ]);
  if (baseImg === null || fieldImg === null) return null;

  const cv = document.createElement("canvas");
  cv.width = W;
  cv.height = H;
  const g = cv.getContext("2d");
  if (g === null) return null;

  g.fillStyle = "#ffffff";
  g.fillRect(0, 0, W, H);

  // --- basemap and field, clipped to the neatline ----------------------------
  const px = (lon: number): number => mx + ((mercX(lon) - xMin) / (xMax - xMin)) * mw;
  const py = (lat: number): number => my + ((yMax - mercY(lat)) / (yMax - yMin)) * mh;

  g.save();
  g.beginPath();
  g.rect(mx, my, mw, mh);
  g.clip();
  g.drawImage(baseImg, mx, my, mw, mh);

  // The field spans the MESH's extent, not the frame's — place it by projecting
  // the mesh corners into this frame rather than assuming the two agree.
  const fx0 = px(opts.mesh.lonMin);
  const fx1 = px(opts.mesh.lonMax);
  const fy0 = py(opts.mesh.latMax);
  const fy1 = py(opts.mesh.latMin);
  g.globalAlpha = 0.78; // the coast must stay readable under the wash
  g.drawImage(fieldImg, fx0, fy0, fx1 - fx0, fy1 - fy0);
  g.globalAlpha = 1;

  // Where the mesh ends INSIDE the frame, say so. Otherwise the edge of the data
  // reads as a change in the sea.
  const edgeIn =
    fx0 > mx + 1 || fx1 < mx + mw - 1 || fy0 > my + 1 || fy1 < my + mh - 1;
  if (edgeIn) {
    g.setLineDash([u(4), u(3)]);
    g.lineWidth = u(1);
    g.strokeStyle = "rgba(22,35,46,0.55)";
    g.strokeRect(fx0, fy0, fx1 - fx0, fy1 - fy0);
    g.setLineDash([]);
  }

  // --- graticule, drawn over the imagery, faint -------------------------------
  const latStep = niceStep(latMax - latMin);
  const lonStep = niceStep(lonMax - lonMin);
  const latDp = decimals(latStep);
  const lonDp = decimals(lonStep);
  const lats: number[] = [];
  for (let v = Math.ceil(latMin / latStep) * latStep; v <= latMax; v += latStep) {
    lats.push(Math.round(v / latStep) * latStep);
  }
  const lons: number[] = [];
  for (let v = Math.ceil(lonMin / lonStep) * lonStep; v <= lonMax; v += lonStep) {
    lons.push(Math.round(v / lonStep) * lonStep);
  }
  g.strokeStyle = "rgba(255,255,255,0.42)";
  g.lineWidth = u(0.7);
  for (const v of lats) {
    const y = py(v);
    g.beginPath();
    g.moveTo(mx, y);
    g.lineTo(mx + mw, y);
    g.stroke();
  }
  for (const v of lons) {
    const x = px(v);
    g.beginPath();
    g.moveTo(x, my);
    g.lineTo(x, my + mh);
    g.stroke();
  }
  g.restore();

  const cx = mx + mw / 2;
  const cy = my + mh / 2;

  // --- the sleeves, same geometry as the map's (map/socks.ts) ------------------
  // Sized against the map's SHORT edge so they stay the same physical mark
  // whatever the plate's aspect.
  const FLY = mh * 0.17;

  /** A sleeve that has been drawn, and where its tag wants to sit. */
  interface Tag {
    dx: number;
    dy: number;
    fly: number;
    text: string;
    colour: string;
  }
  const tags: Tag[] = [];

  const drawSleeve = (
    s: SiteMapOptions["wind"],
    fallbackLabel: string,
    colour: string,
  ): void => {
    if (s?.dirDeg == null || s.frac == null) return;
    const heading = s.dirDeg + 180; // quoted FROM, drawn the way it is going
    const a = (heading * Math.PI) / 180;
    const dx = Math.sin(a);
    const dy = -Math.cos(a);
    const nx = -dy;
    const ny = dx;
    const fly = FLY * (0.3 + 0.7 * Math.max(0, Math.min(1, s.frac)));
    const r0 = u(1.6);
    const r1 = u(6);
    g.beginPath();
    g.moveTo(cx + nx * r0, cy + ny * r0);
    g.lineTo(cx + dx * fly + nx * r1, cy + dy * fly + ny * r1);
    g.lineTo(cx + dx * fly - nx * r1, cy + dy * fly - ny * r1);
    g.lineTo(cx - nx * r0, cy - ny * r0);
    g.closePath();
    g.lineJoin = "round";
    g.lineWidth = u(2.4);
    g.strokeStyle = "rgba(255,255,255,0.9)";
    g.stroke();
    g.fillStyle = colour;
    g.fill();

    const name = (s.label || fallbackLabel).toUpperCase();
    tags.push({
      dx, dy, fly,
      text: s.text === undefined ? name : `${name} ${s.text}`,
      colour,
    });
  };
  drawSleeve(opts.wind, "wind", WIND_INK);
  drawSleeve(opts.sea, "sea", SEA_INK);

  // --- the site ---------------------------------------------------------------
  g.beginPath();
  g.arc(cx, cy, u(3.4), 0, Math.PI * 2);
  g.fillStyle = INK;
  g.fill();
  g.lineWidth = u(1.6);
  g.strokeStyle = "#ffffff";
  g.stroke();

  // --- the sleeve tags ---------------------------------------------------------
  // Two identically-shaped wedges in two colours are not self-describing. Without
  // these the plate needs its caption read before the picture can be, which is
  // backwards — and the reason the colour key exists on the console's own socks.
  //
  // The tag sits off the fly end and is pushed further out when the two would
  // collide, which happens exactly when wind and sea are ALIGNED — the common
  // case, so it is the case that has to work.
  if (tags.length > 0) {
    g.font = `700 ${u(8.5)}px ui-monospace, SFMono-Regular, monospace`;
    g.textBaseline = "middle";
    g.textAlign = "left";
    const padX = u(4);
    const boxH = u(13);
    interface Placed extends Tag { w: number }
    const placed: Placed[] = tags.map((t) => ({
      ...t,
      w: g.measureText(t.text).width + padX * 2,
    }));
    const anchor = (t: Placed, out: number): { x: number; y: number } => ({
      x: cx + t.dx * (t.fly + out),
      y: cy + t.dy * (t.fly + out),
    });
    const gaps = placed.map(() => u(12));
    for (let n = 0; n < 40 && placed.length === 2; n++) {
      const a = anchor(placed[0], gaps[0]);
      const b = anchor(placed[1], gaps[1]);
      const hitX = Math.abs(a.x - b.x) < (placed[0].w + placed[1].w) / 2;
      const hitY = Math.abs(a.y - b.y) < boxH * 1.3;
      if (!hitX || !hitY) break;
      // Move the NEARER tag outward, so the pair reads from the centre out.
      const i = placed[0].fly + gaps[0] <= placed[1].fly + gaps[1] ? 0 : 1;
      gaps[i] += u(5);
    }
    placed.forEach((t, i) => {
      const p = anchor(t, gaps[i]);
      // Keep the box inside the neatline: a tag half off the plate is worse than
      // a tag sitting a little wide of its sleeve.
      const x = Math.max(mx + u(3), Math.min(mx + mw - t.w - u(3), p.x - t.w / 2));
      const y = Math.max(my + boxH / 2 + u(3), Math.min(my + mh - boxH / 2 - u(3), p.y));
      g.fillStyle = "rgba(255,255,255,0.93)";
      g.fillRect(x, y - boxH / 2, t.w, boxH);
      g.strokeStyle = t.colour;
      g.lineWidth = u(1);
      g.strokeRect(x, y - boxH / 2, t.w, boxH);
      g.fillStyle = t.colour;
      g.fillText(t.text, x + padX, y);
    });
  }

  // --- neatline and labelled ticks --------------------------------------------
  // Ticks on all four sides, labels on two. The labels are the answer to "where
  // is this", so they sit on paper in the margin, not on the imagery.
  g.strokeStyle = INK;
  g.lineWidth = u(1);
  g.strokeRect(mx, my, mw, mh);

  g.fillStyle = INK;
  g.font = `600 ${u(9)}px ui-monospace, SFMono-Regular, monospace`;
  const tick = u(4);
  g.textAlign = "right";
  g.textBaseline = "middle";
  for (const v of lats) {
    const y = py(v);
    g.beginPath();
    g.moveTo(mx, y); g.lineTo(mx + tick, y);
    g.moveTo(mx + mw, y); g.lineTo(mx + mw - tick, y);
    g.stroke();
    g.fillText(fmtLat(v, latDp), mx - u(5), y);
  }
  g.textAlign = "center";
  g.textBaseline = "top";
  for (const v of lons) {
    const x = px(v);
    g.beginPath();
    g.moveTo(x, my + mh); g.lineTo(x, my + mh - tick);
    g.moveTo(x, my); g.lineTo(x, my + tick);
    g.stroke();
    g.fillText(fmtLon(v, lonDp), x, my + mh + u(6));
  }

  // --- plaques: north arrow, scale bar, locator --------------------------------
  const plaque = (x: number, y: number, w: number, h: number): void => {
    g.fillStyle = "rgba(255,255,255,0.9)";
    g.fillRect(x, y, w, h);
    g.strokeStyle = INK;
    g.lineWidth = u(0.8);
    g.strokeRect(x, y, w, h);
  };

  // The stamp — top-left, the corner the north arrow and the locator leave free.
  const stamp = opts.stamp ?? [];
  if (stamp.length > 0) {
    g.font = `600 ${u(8.5)}px ui-monospace, SFMono-Regular, monospace`;
    g.textAlign = "left";
    g.textBaseline = "middle";
    const lineH = u(12);
    const wid = Math.max(...stamp.map((s) => g.measureText(s).width)) + u(12);
    const hei = lineH * stamp.length + u(8);
    const stX = mx + u(8);
    const stY = my + u(8);
    plaque(stX, stY, wid, hei);
    g.fillStyle = INK;
    stamp.forEach((s, i) => {
      g.fillText(s, stX + u(6), stY + u(4) + lineH * (i + 0.5));
    });
  }

  // North.
  const nW = u(20);
  const nH = u(30);
  const nX = mx + mw - u(8) - nW;
  const nY = my + u(8);
  plaque(nX, nY, nW, nH);
  g.fillStyle = INK;
  g.beginPath();
  g.moveTo(nX + nW / 2, nY + u(5));
  g.lineTo(nX + nW / 2 - u(5), nY + u(18));
  g.lineTo(nX + nW / 2 + u(5), nY + u(18));
  g.closePath();
  g.fill();
  g.font = `700 ${u(8)}px ui-monospace, SFMono-Regular, monospace`;
  g.textAlign = "center";
  g.textBaseline = "alphabetic";
  g.fillText("N", nX + nW / 2, nY + u(27));

  // Scale: nautical miles per pixel at the CENTRE latitude, which is where the
  // bar is drawn and therefore where it is true. Mercator scale varies with
  // latitude, so a bar labelled for the whole frame would be wrong at its edges.
  const nmPerPx = ((latMax - latMin) * 60) / mh;
  const targets = [5, 10, 20, 25, 50, 100, 200, 250, 500];
  const want = nmPerPx * (mw * 0.18);
  const barNm = targets.reduce((a, b) => (Math.abs(b - want) < Math.abs(a - want) ? b : a), targets[0]);
  const barPx = barNm / nmPerPx;
  const sW = barPx + u(16);
  const sH = u(22);
  const sX = mx + u(8);
  const sY = my + mh - u(8) - sH;
  plaque(sX, sY, sW, sH);
  // Alternating black/white halves: the convention, and it reads at a glance
  // where a plain rule does not.
  const bx = sX + u(8);
  const by = sY + sH - u(6);
  g.fillStyle = INK;
  g.fillRect(bx, by - u(3), barPx, u(3));
  g.fillStyle = "#ffffff";
  g.fillRect(bx + barPx / 2, by - u(3), barPx / 2, u(3));
  g.strokeStyle = INK;
  g.lineWidth = u(0.8);
  g.strokeRect(bx, by - u(3), barPx, u(3));
  g.fillStyle = INK;
  g.font = `600 ${u(8)}px ui-monospace, SFMono-Regular, monospace`;
  g.textAlign = "left";
  g.textBaseline = "alphabetic";
  g.fillText(`0`, bx, sY + u(9));
  g.textAlign = "right";
  g.fillText(`${barNm} nm`, bx + barPx, sY + u(9));

  // Locator. Skipped rather than faked if it did not load — the plate is still
  // fully coordinated by its graticule without it.
  if (insetImg !== null) {
    const iX = mx + mw - u(8) - iSide;
    const iY = my + mh - u(8) - iSide;
    g.save();
    g.beginPath();
    g.rect(iX, iY, iSide, iSide);
    g.clip();
    g.drawImage(insetImg, iX, iY, iSide, iSide);
    g.restore();
    g.strokeStyle = INK;
    g.lineWidth = u(1);
    g.strokeRect(iX, iY, iSide, iSide);

    // This frame's footprint on the locator. Below a few pixels a rectangle is
    // indistinguishable from a smudge, so it becomes a crosshair instead — the
    // job is "here", not "exactly this shape".
    const ipx = (x: number): number => iX + ((x - ixMin) / (ixMax - ixMin)) * iSide;
    const ipy = (y: number): number => iY + ((iyMax - y) / (iyMax - iyMin)) * iSide;
    const bxx = ipx(xMin);
    const byy = ipy(yMax);
    const bww = ipx(xMax) - bxx;
    const bhh = ipy(yMin) - byy;
    g.lineWidth = u(1.6);
    if (bww >= u(6) && bhh >= u(6)) {
      g.strokeStyle = "#ffffff";
      g.strokeRect(bxx - u(0.8), byy - u(0.8), bww + u(1.6), bhh + u(1.6));
      g.strokeStyle = "#b3261e";
      g.strokeRect(bxx, byy, bww, bhh);
    } else {
      const hx = ipx(xC);
      const hy = ipy((yMin + yMax) / 2);
      g.strokeStyle = "#ffffff";
      g.lineWidth = u(3);
      g.beginPath();
      g.moveTo(hx - u(6), hy); g.lineTo(hx + u(6), hy);
      g.moveTo(hx, hy - u(6)); g.lineTo(hx, hy + u(6));
      g.stroke();
      g.strokeStyle = "#b3261e";
      g.lineWidth = u(1.4);
      g.stroke();
    }
  }

  return cv.toDataURL("image/png");
}

/** The caption a site map needs so its colours mean something. */
export function siteMapCaption(opts: SiteMapOptions): string {
  const bits: string[] = [];
  const layerWord =
    opts.layer === "limits" ? "workability against the armed limits" :
    opts.layer === "wind" ? "wind speed" : "significant wave height";
  bits.push(`Field: ${layerWord}.`);
  const arrow = (s: SiteMapOptions["wind"], name: string): void => {
    if (s?.dirDeg == null) return;
    bits.push(`${name} from ${compass16(s.dirDeg)} ${Math.round(s.dirDeg)}°.`);
  };
  arrow(opts.wind, "Wind");
  arrow(opts.sea, "Sea");
  bits.push("Sleeves fly the way the water is going; bearings are quoted the way they come from.");
  bits.push("Inset locates the frame; the dashed line, where it appears, is the edge of the field grid.");
  return bits.join(" ");
}
